import { Dispatch, FC, SetStateAction, useEffect } from 'react';
import cn from 'classnames';
import { DarkModeSwitch } from 'src/entity/DarkModeSwitch';
import { ReactComponent as ArrowIcon } from 'src/shared/assets/icons/kit/arrow.svg';
import { ReactComponent as CheckIcon } from 'src/shared/assets/icons/kit/check.svg';
import { useLogout } from 'src/shared/lib/hooks/useLogout';

interface HeadItemsProps {
  fullWidth: boolean;
  setFullWidth: Dispatch<SetStateAction<boolean>>;
}

export const HeadItems: FC<HeadItemsProps> = ({ fullWidth, setFullWidth }) => {
  const { handleLogout } = useLogout();
  const fullscreenLabel = fullWidth ? 'Выйти из полноэкранного режима' : 'Полноэкранный режим';

  const handleFullWidth = () => {
    const root = document.querySelector('#root');
    if (fullWidth) {
      document?.exitFullscreen && document.exitFullscreen();
    } else {
      root?.requestFullscreen && root.requestFullscreen();
    }
  };

  useEffect(() => {
    const fullScreenChange = () => setFullWidth(Boolean(document.fullscreenElement));

    document.addEventListener('fullscreenchange', fullScreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', fullScreenChange);
    };
  }, []);

  return (
    <>
      <DarkModeSwitch classNames="z-10 mb-3 mt-0.5 lg:absolute lg:left-2.5 lg:top-4 lg:mb-0" />

      <div className="z-10 ml-auto mt-0.5 flex max-w-full flex-wrap items-center justify-end gap-3 lg:absolute lg:right-2.5 lg:top-4 lg:mt-0 lg:max-w-none lg:flex-nowrap">
        <button
          type="button"
          className="ml-auto flex min-w-0 items-center gap-2 text-left"
          onClick={handleFullWidth}
          aria-label={fullscreenLabel}
          title={fullscreenLabel}
        >
          <div
            className={cn(
              { 'bg-violet-1500': fullWidth, 'bg-white': !fullWidth },
              'flex h-6 w-6 shrink-0 items-center justify-center rounded border border-violet-1500',
            )}
          >
            {fullWidth && <CheckIcon className="h-4 w-4 stroke-white" />}
          </div>
          <div className="font-gothampro-400 min-w-0 text-sm text-black dark:text-white">
            <span className="hidden xl:inline">{fullscreenLabel}</span>
            <span className="xl:hidden">{fullWidth ? 'Выйти' : 'Экран'}</span>
          </div>
        </button>

        <button
          type="button"
          className="group flex shrink-0 items-center gap-2"
          onClick={handleLogout}
          aria-label="Выход"
        >
          <div className="font-gothampro-500 text-grey-400 transition-all group-hover:text-grey-300">
            <span className="hidden sm:inline">Выход</span>
            <span className="sm:hidden">Выйти</span>
          </div>
          <ArrowIcon className="h-4 w-4 fill-grey-400 transition-all group-hover:fill-grey-300" />
        </button>
      </div>
    </>
  );
};
